import { legalActions, minRaiseTo } from '@/engine/rules';
import type { HandState, LegalAction, PlayerId } from '@/engine/types';
import { potSize } from './potOdds';

/** Sizing preset fractions of pot. All-in is a separate legal action. */
export const SIZING_FRACTIONS = [0.33, 0.5, 0.66, 1.0, 1.5] as const;
export type SizingLabel = '1/3' | '1/2' | '2/3' | 'pot' | '1.5pot' | 'allin';

export type SizingOption = {
  label: SizingLabel;
  legal: LegalAction;
};

const LABEL_FOR_FRACTION: Record<number, SizingLabel> = {
  0.33: '1/3',
  0.5: '1/2',
  0.66: '2/3',
  1.0: 'pot',
  1.5: '1.5pot',
};

/**
 * Enumerate legal sizing options for the current actor. Clips below-min-raise
 * targets up to the engine's min-raise rule; skips sizes above all-in.
 */
export function enumerateSizings(state: HandState, playerId: PlayerId): SizingOption[] {
  const legals = legalActions(state, playerId);
  if (legals.length === 0) return [];
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return [];

  const out: SizingOption[] = [];
  const pot = potSize(state);
  const facingBet = state.betting.currentBet - player.streetContribution > 0;

  const raiseLegal = legals.find((l) => l.type === 'raise');
  const betLegal = legals.find((l) => l.type === 'bet');
  const allinLegal = legals.find((l) => l.type === 'allin');

  if (facingBet && raiseLegal && raiseLegal.min !== undefined && raiseLegal.max !== undefined) {
    const minRaise = minRaiseTo(state);
    for (const frac of SIZING_FRACTIONS) {
      const raiseTo = Math.max(minRaise, Math.floor(state.betting.currentBet + pot * frac));
      if (raiseTo <= raiseLegal.max) {
        out.push({
          label: LABEL_FOR_FRACTION[frac] as SizingLabel,
          legal: { type: 'raise', min: raiseTo, max: raiseTo },
        });
      }
    }
  } else if (!facingBet && betLegal && betLegal.min !== undefined && betLegal.max !== undefined) {
    for (const frac of SIZING_FRACTIONS) {
      const betAmount = Math.max(betLegal.min, Math.floor(pot * frac));
      if (betAmount <= betLegal.max) {
        out.push({
          label: LABEL_FOR_FRACTION[frac] as SizingLabel,
          legal: { type: 'bet', min: betAmount, max: betAmount },
        });
      }
    }
  }

  if (allinLegal) {
    out.push({ label: 'allin', legal: allinLegal });
  }

  return out;
}
